import { api } from './api.js';
import { esc, fmt$, fmtDate, isOverdue, priorityBadge, getWeekBounds } from './utils.js';

// Roadmap spec: "Bold accent numbers inline" in the Voice summary sentence
// itself, not just in the separate expandable breakdown grid. The voice
// string is server-generated from real calculations (not raw user input),
// but still run through esc() first for defense in depth — this only ever
// wraps already-escaped numeric substrings in markup, never anything from
// the match groups that could carry HTML.
function boldNumbers(escapedText) {
  return escapedText.replace(
    /(\$[\d,]+(?:\.\d+)?[KMkm]?|\b\d+(?:\.\d+)?%|\b\d+\s+months?\b)/g,
    '<strong style="color:var(--accent)">$1</strong>',
  );
}

export async function renderPulse(el) {
  el.innerHTML = skeleton();

  // Get Monday-Sunday boundaries for the current week
  const today = new Date();
  const { weekStart: monStr, weekEnd: sunStr } = getWeekBounds(today);

  let [
    data,
    progressData,
    settingsData,
    incomeData,
    expenseData,
    tasksData,
    nwData,
    allocs,
    cats,
    customBlocks,
    schoolBlocks,
    recurringBlocks,
    rothList,
    weeklyReviews
  ] = await Promise.all([
    api.dashboard.pulse().catch(() => null),
    api.dashboard.progress().catch(() => null),
    api.settings.get().catch(() => null),
    api.income.list().catch(() => []),
    api.expenses.list().catch(() => []),
    api.tasks.list().catch(() => []),
    api.networth.list().catch(() => []),
    api.settings.getTimeAlloc().catch(() => []),
    api.categories().catch(() => []),
    api.blocks.list({ from: monStr, to: sunStr }).catch(() => []),
    api.blocks.schoolBlocks({ from: monStr, to: sunStr }).catch(() => []),
    api.blocks.recurringBlocks({ from: monStr, to: sunStr }).catch(() => []),
    api.roth.list().catch(() => []),
    api.weeklyReviews.list().catch(() => []),
  ]);

  const thisWeekReview = weeklyReviews.find(r => r.weekStart === monStr);
  const reviewedThisWeek = !!thisWeekReview;
  const weekPlanned = thisWeekReview?.weekPlanned ?? false;

  // Materialized rule instances already come back from api.blocks.list() too —
  // drop them so recurringBlocks (which has the correct, de-duped set) is the
  // single source of truth for this week's category-hours allocation.
  customBlocks = [...customBlocks.filter(b => !b.ruleId), ...recurringBlocks];

  const scheduledTaskIds = new Set(customBlocks.map(b => b.taskId).filter(Boolean));
  const unscheduledHighPriorityCount = tasksData.filter(t =>
    t.priority === 'high' && (t.status === 'open' || t.status === 'in_progress') && !scheduledTaskIds.has(t.id)
  ).length;

  if (!data) {
    el.innerHTML = `<p style="color:var(--negative)">Failed to load dashboard data.</p>`;
    return;
  }

  const todayStr  = today.toISOString().split('T')[0];
  const dateLabel = today.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' });

  const payEnd    = new Date(data.payPeriod.end + 'T00:00:00Z');
  const todayDate = new Date(todayStr + 'T00:00:00Z');
  const daysLeft  = Math.max(0, Math.round((payEnd - todayDate) / 86_400_000));
  const surplusColor = data.surplusThisMonth >= 0 ? 'var(--positive)' : 'var(--negative)';
  const surplusSign  = data.surplusThisMonth >= 0 ? '+' : '';

  // ── SAMPLE DATA BANNER ──────────────────────────────────────────────────
  const sampleBanner = settingsData?.hasSampleData ? `
    <div class="sample-banner">
      <span class="sample-banner-label">⚠ SAMPLE DATA — exploring the app</span>
      <span class="sample-banner-clear" id="clear-sample">Clear and start fresh</span>
    </div>` : '';

  // ── BETWEEN-SESSION BANNERS (one at a time) ───────────────────────────
  const banner = buildBanner(data, nwData, settingsData, reviewedThisWeek, weekPlanned, unscheduledHighPriorityCount);

  // ── FIRST-WEEK CHECKLIST ──────────────────────────────────────────────
  const checklist = buildChecklist(settingsData, incomeData, tasksData, expenseData, nwData, customBlocks, schoolBlocks);

  // -- EMPTY STATE GUIDANCE ------------------------------------------------
  const isEmptyState = !settingsData?.hasSampleData
    && (data.surplusThisMonth === 0)
    && (!incomeData || incomeData.length === 0)
    && (!data.openTasks || data.openTasks.length === 0);

  const emptyStateCard = isEmptyState ? `
    <div class="card" style="border:1.5px dashed var(--bg-tertiary);background:transparent;text-align:center;padding:var(--space-6)">
      <div style="font-size:2rem;margin-bottom:var(--space-3)">👋</div>
      <div style="font-weight:700;font-size:1.05rem;margin-bottom:var(--space-2)">Let's get you set up</div>
      <div style="font-size:.85rem;color:var(--text-secondary);margin-bottom:var(--space-5)">A few first steps to make your dashboard come alive</div>
      <div style="display:flex;flex-direction:column;gap:var(--space-2)">
        <button class="btn btn-primary" id="empty-log-income" style="justify-content:center;padding:10px">
          💰 Log your first paycheck
        </button>
        <button class="btn btn-ghost" id="empty-add-block" style="justify-content:center;padding:10px">
          📅 Add a time block to your schedule
        </button>
        <button class="btn btn-ghost" id="empty-go-settings" style="justify-content:center;padding:10px">
          ⚙️ Configure your income &amp; goals
        </button>
      </div>
    </div>` : '';


  // ── TIME ALLOCATIONS COMPUTATIONS ──────────────────────────────────────
  const getBlockHours = (b) => {
    if (!b.startTime || !b.endTime) return 0;
    const [sh, sm] = b.startTime.split(':').map(Number);
    const [eh, em] = b.endTime.split(':').map(Number);
    return Math.max(0, (eh + em/60) - (sh + sm/60));
  };

  const catMap = {};
  for (const c of cats) {
    catMap[c.id] = {
      id: c.id,
      name: c.name,
      color: c.color,
      isSystem: c.isSystem,
      target: 0,
      min: null,
      max: null,
      actual: 0
    };
  }

  for (const a of allocs) {
    if (catMap[a.categoryId]) {
      catMap[a.categoryId].target = a.weeklyHours;
      catMap[a.categoryId].min    = a.minWeeklyHours ?? null;
      catMap[a.categoryId].max    = a.maxWeeklyHours ?? null;
    }
  }

  for (const b of customBlocks) {
    if (b.categoryId && catMap[b.categoryId]) {
      catMap[b.categoryId].actual += getBlockHours(b);
    }
  }

  const schoolCat = cats.find(c => c.name.toLowerCase() === 'school');
  if (schoolCat && catMap[schoolCat.id]) {
    for (const b of schoolBlocks) {
      catMap[schoolCat.id].actual += getBlockHours(b);
    }
  }

  const allocData = Object.values(catMap).filter(c => c.target > 0 || c.actual > 0);

  // ── ROTH IRA PROJECTIONS ──────────────────────────────────────────────
  const currentYear = today.getFullYear();
  const rothThisYear = rothList
    .filter(r => r.year === currentYear || r.date.startsWith(currentYear.toString()))
    .reduce((sum, r) => sum + r.amount, 0);
  const rothLimit = 7000;
  const rothPct = Math.min(100, Math.round((rothThisYear / rothLimit) * 100));
  const rothRemaining = Math.max(0, rothLimit - rothThisYear);

  // ── TARGET DATE ────────────────────────────────────────────────────────
  const projectedMonths = progressData?.future?.projections?.moderate;
  let projectedDateLabel = 'Never';
  if (projectedMonths && projectedMonths !== Infinity) {
    const projDate = new Date();
    projDate.setMonth(projDate.getMonth() + projectedMonths);
    projectedDateLabel = projDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  el.innerHTML = `
    <div style="max-width:720px;margin:0 auto">

      ${sampleBanner}
      ${emptyStateCard}
      ${banner}
      ${checklist}

      <div style="margin-bottom:var(--space-5);display:flex;justify-content:space-between;align-items:flex-end">
        <div>
          <h1 style="font-size:1.3rem;font-weight:700;margin:0">${esc(dateLabel)}</h1>
          <div style="margin-top:4px">
            ${data.payPeriod.isPayday    ? '<span class="badge badge-done">💰 Payday</span> ' : ''}
            ${data.payPeriod.isCommission ? '<span class="badge" style="background:rgba(18,185,129,.12);color:var(--positive)">Commission day</span>' : ''}
          </div>
        </div>
      </div>

      <!-- ── ASSISTANT VOICE CARD ────────────────────────────────────────── -->
      <div class="card voice-card" style="background:linear-gradient(135deg, rgba(var(--accent-rgb),0.08) 0%, rgba(139,92,246,0.04) 100%);border:1px solid rgba(var(--accent-rgb),.15);border-radius:var(--radius-xl);padding:var(--space-4)">
        <div style="display:flex;align-items:center;gap:var(--space-2);margin-bottom:var(--space-2)">
          <span style="font-size:1.2rem">✨</span>
          <span style="font-weight:700;font-size:.9rem;color:var(--text-primary);text-transform:uppercase;letter-spacing:.05em">Assistant Voice</span>
        </div>
        <p style="font-size:.9rem;line-height:1.55;color:var(--text-secondary);margin:0;margin-bottom:var(--space-3);font-style:italic">
          "${boldNumbers(esc(progressData?.future?.voice ?? 'Welcome back. Everything in your personal OS is running steady. Take a quick look below at your week.'))}"
        </p>
        <button class="btn btn-ghost" id="voice-expand-btn" style="padding:4px 8px;font-size:.7rem;color:var(--accent);display:flex;align-items:center;gap:4px">
          <span>Show Status Breakdown</span>
          <span id="voice-expand-icon">↓</span>
        </button>
        <div id="voice-details" style="display:none;margin-top:var(--space-3);padding-top:var(--space-3);border-top:1px solid var(--bg-tertiary)">
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-3);font-size:.8rem">
            <div>
              <div style="color:var(--text-tertiary)">Surplus this month (so far)</div>
              <div style="font-weight:700;color:${progressData?.now?.surplusThisMonth >= 0 ? 'var(--positive)' : 'var(--negative)'}">
                ${fmt$(progressData?.now?.surplusThisMonth ?? 0)}
              </div>
            </div>
            <div>
              <div style="color:var(--text-tertiary)">Trading P&L (Month)</div>
              <div style="font-weight:700;color:${progressData?.now?.tradePnlThisMonth >= 0 ? 'var(--positive)' : 'var(--negative)'}">
                ${fmt$(progressData?.now?.tradePnlThisMonth ?? 0)}
              </div>
            </div>
            <div>
              <div style="color:var(--text-tertiary)">Net Income (Month)</div>
              <div style="font-weight:700;color:var(--text-primary)">
                ${fmt$(progressData?.now?.monthIncome ?? 0)}
              </div>
            </div>
            <div>
              <div style="color:var(--text-tertiary)">Expenses (Month)</div>
              <div style="font-weight:700;color:var(--text-primary)">
                ${fmt$(progressData?.now?.monthExpenses ?? 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── SURPLUS DISPLAY ─────────────────────────────────────────────── -->
      <div class="card card-bordered-${data.surplusThisMonth >= 0 ? 'positive' : 'negative'}">
        <div class="card-header">
          <span class="card-title">Surplus this month</span>
          <!-- UX_AUDIT F-5/F-6: this figure and the one driving every projection were
               both labelled just "Surplus" while disagreeing in sign (-$1,143 here,
               +$1,890 there). Each label now names its timeframe and whether it is
               measured or estimated. -->
          <span style="font-size:.75rem;color:var(--text-secondary)">so far</span>
        </div>
        <div class="hero-number" style="color:${surplusColor}">${surplusSign}${fmt$(data.surplusThisMonth)}</div>
        <div class="hero-label">Pay period ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} · next payday ${fmtDate(data.payPeriod.payday)}</div>

        ${(() => {
          const monthIncome   = progressData?.now?.monthIncome ?? 0;
          const monthExpenses = progressData?.now?.monthExpenses ?? 0;
          // Both bars are drawn against one shared scale - the larger of the two - so
          // their lengths are directly comparable. The income fill used to be hardcoded
          // at 100%: it encoded nothing, and with $0 income logged it rendered a full
          // bar above the figure $0 while the larger expense figure got an empty one.
          const barScale = Math.max(monthIncome, monthExpenses);
          const barPct   = (v) => barScale > 0 ? Math.min(100, Math.round((v / barScale) * 100)) : 0;
          // "% of income" is undefined when no income is logged - print no ratio at all
          // rather than "0% of income", which reads as "spent nothing".
          const ratioLabel = monthIncome > 0
            ? ` (${Math.round((monthExpenses / monthIncome) * 100)}% of income)`
            : '';
          return `
          <div style="margin-top:var(--space-4);padding-top:var(--space-3);border-top:1px solid var(--bg-tertiary)">
            <div style="display:flex;justify-content:space-between;font-size:.75rem;margin-bottom:4px">
              <span style="color:var(--text-secondary)">Income logged</span>
              <span style="color:var(--positive);font-weight:600">${fmt$(monthIncome)}</span>
            </div>
            <div class="progress-bar" style="height:5px;background:var(--bg-tertiary);border-radius:99px;margin-bottom:var(--space-2)">
              <div class="progress-fill" style="width:${barPct(monthIncome)}%;height:100%;border-radius:99px;background:var(--positive)"></div>
            </div>
            <div style="display:flex;justify-content:space-between;font-size:.75rem;margin-bottom:4px">
              <span style="color:var(--text-secondary)">Expenses${ratioLabel}</span>
              <span style="color:var(--negative);font-weight:600">${fmt$(monthExpenses)}</span>
            </div>
            <div class="progress-bar" style="height:5px;background:var(--bg-tertiary);border-radius:99px">
              <div class="progress-fill" style="width:${barPct(monthExpenses)}%;height:100%;border-radius:99px;background:${monthExpenses > monthIncome ? 'var(--negative)' : 'var(--positive)'}"></div>
            </div>
          </div>`;
        })()}
      </div>

      <!-- ── TODAY'S SCHEDULE ────────────────────────────────────────────── -->
      <div class="card">
        <div class="card-header">
          <span class="card-title">Today's schedule</span>
          <span style="font-size:.75rem;color:var(--text-secondary)">${data.schedule.length} block${data.schedule.length !== 1 ? 's' : ''}</span>
        </div>
        ${data.schedule.length === 0
          ? `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No blocks today — free day</p>`
          : data.schedule.map(b => {
              // b.isSchool never existed on this payload (dashboard.ts's
              // /pulse route tags school entries with `type: 'school'`) —
              // this color-coding silently never fired. Found while adding
              // the spec's lock icon; fixed both in the same pass.
              const isSchool = b.type === 'school';
              const color = isSchool ? 'var(--cat-school)' : 'var(--accent)';
              return `
                <div class="schedule-item">
                  <div class="schedule-time">${esc(b.startTime)}–${esc(b.endTime)}</div>
                  <div class="schedule-bar" style="background:${color}"></div>
                  <div class="schedule-body">
                    <div class="schedule-title">${isSchool ? '🔒 ' : ''}${esc(b.title)}</div>
                    ${b.notes || b.location ? `<div class="schedule-sub">${esc(b.notes ?? b.location)}</div>` : ''}
                  </div>
                </div>`;
            }).join('')
        }
      </div>

      <!-- ── THIS WEEK ALLOCATIONS ────────────────────────────────────────── -->
      <div class="card">
        <div class="card-header">
          <span class="card-title">This week allocations</span>
          <span style="font-size:.75rem;color:var(--text-secondary)">Time utilization targets</span>
        </div>
        ${allocData.length === 0
          ? `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No allocation targets or blocks this week.</p>`
          : allocData.map(alloc => {
              const pct = alloc.target > 0 ? Math.min(100, Math.round((alloc.actual / alloc.target) * 100)) : 100;
              // Scale the bar to whichever is largest (actual/max/target) so the
              // min-max band and the fill both stay within 0-100% of the same axis.
              const scale = Math.max(alloc.actual, alloc.max ?? 0, alloc.target, 0.1) * 1.1;
              const actualPct = Math.min(100, (alloc.actual / scale) * 100);
              const bandStart = alloc.min != null ? (alloc.min / scale) * 100 : null;
              const bandEnd   = alloc.max != null ? (alloc.max / scale) * 100 : null;
              const underMin  = alloc.min != null && alloc.actual < alloc.min;
              const overMax   = alloc.max != null && alloc.actual > alloc.max;
              const fillColor = overMax ? 'var(--negative)' : underMin ? 'var(--caution)' : esc(alloc.color);
              const rangeLabel = (alloc.min != null || alloc.max != null)
                ? ` <span style="color:var(--text-tertiary)">(${alloc.min ?? 0}–${alloc.max ?? '∞'}h range)</span>` : '';
              // On-track: within the min/max band if one's set, else close
              // enough to the flat target — a short visual indicator instead
              // of making the user parse the percentage text themselves.
              const onTrack = (alloc.min != null || alloc.max != null)
                ? !underMin && !overMax
                : pct >= 90;
              return `
                <div style="margin-bottom:var(--space-3)">
                  <div style="display:flex;justify-content:space-between;font-size:.8rem;margin-bottom:4px">
                    <span style="font-weight:600;display:flex;align-items:center;gap:6px">
                      <span style="width:8px;height:8px;border-radius:50%;background:${esc(alloc.color)}"></span>
                      ${esc(alloc.name)}
                      ${onTrack ? '<span style="color:var(--positive)" title="On track">✓</span>' : ''}
                    </span>
                    <span style="color:var(--text-secondary)">
                      ${+alloc.actual.toFixed(1)}h / ${alloc.target}h (${pct}%)${rangeLabel}
                    </span>
                  </div>
                  <div class="progress-bar" style="position:relative;height:6px;background:var(--bg-tertiary);border-radius:99px">
                    ${bandStart != null && bandEnd != null ? `
                      <div style="position:absolute;left:${bandStart}%;width:${bandEnd - bandStart}%;height:100%;background:rgba(255,255,255,.12);border-radius:99px"></div>
                    ` : ''}
                    <div class="progress-fill" style="width:${actualPct}%;height:100%;border-radius:99px;background:${fillColor}"></div>
                  </div>
                </div>`;
            }).join('')
        }
      </div>

      <!-- ── FOCUS TASKS ──────────────────────────────────────────────────── -->
      <div class="card">
        <div class="card-header">
          <span class="card-title">Focus tasks</span>
          <a href="#" id="view-all-tasks" style="font-size:.75rem;color:var(--accent)">See all →</a>
        </div>
        ${data.openTasks.length === 0
          ? `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No open tasks — <a href="#" id="add-first-task" style="color:var(--accent)">add one</a></p>`
          : data.openTasks.slice(0, 8).map(t => `
              <div class="task-item">
                <div class="task-check${t.status === 'done' ? ' checked' : ''}" data-task-id="${esc(t.id)}"></div>
                <div class="task-body">
                  <div class="task-title">${esc(t.title)}</div>
                  <div class="task-meta">
                    ${priorityBadge(t.priority)}
                    ${t.dueDate ? `<span class="task-due${isOverdue(t.dueDate) ? ' overdue' : ''}">Due ${fmtDate(t.dueDate)}</span>` : ''}
                    ${t.estimatedHours ? `<span class="task-due">${+t.estimatedHours}h</span>` : ''}
                  </div>
                </div>
              </div>`).join('')
        }
      </div>

      <!-- ── PATH FORWARD CARD ────────────────────────────────────────────── -->
      <div class="card path-forward-card" style="border:1px solid rgba(82,106,240,0.15);border-radius:var(--radius-xl);padding:var(--space-4)">
        <div class="card-header" style="margin-bottom:var(--space-3)">
          <span class="card-title">💡 Path Forward</span>
          <span style="font-size:.75rem;color:var(--text-tertiary)">Milestones & Goals</span>
        </div>

        <div style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:var(--space-4)">
          <span style="font-size:.85rem;color:var(--text-secondary)">Current Net Worth <span style="color:var(--text-tertiary)">(est.)</span></span>
          <span style="font-size:1.3rem;font-weight:800;color:var(--text-tertiary);font-family:var(--font-mono)">
            ${fmt$(progressData?.future?.netWorth ?? 0)}
          </span>
        </div>

        ${(() => {
          const mile = progressData?.future?.milestone;
          if (!mile) return '';
          const milePct = Math.round((mile.progress ?? 0) * 100);
          return `
            <div style="margin-bottom:var(--space-4)">
              <div style="display:flex;justify-content:space-between;font-size:.75rem;margin-bottom:4px">
                <span style="color:var(--text-secondary)">Milestone: <strong>${esc(mile.current?.label ?? 'Foundation')}</strong></span>
                <span style="color:var(--text-primary)">${milePct}% to <strong>${esc(mile.next?.label ?? 'First Comma')}</strong></span>
              </div>
              <div class="progress-bar" style="height:6px;background:var(--bg-tertiary);border-radius:99px">
                <div class="progress-fill" style="width:${milePct}%;height:100%;border-radius:99px;background:linear-gradient(90deg, var(--accent) 0%, #8B5CF6 100%)"></div>
              </div>
              <div style="display:flex;justify-content:space-between;font-size:.7rem;color:var(--text-tertiary);margin-top:2px">
                <span>${fmt$(mile.current?.amount ?? 0)}</span>
                <span>${fmt$(mile.next?.amount ?? 0)}</span>
              </div>
            </div>
          `;
        })()}

        <div style="border-top:1px solid var(--bg-tertiary);padding-top:var(--space-3);margin-bottom:var(--space-4)">
          <div style="display:flex;justify-content:space-between;align-items:center">
            <div>
              <div style="font-size:.8rem;font-weight:700;color:var(--text-primary)">Compounded Goal ($1,000,000)</div>
              <div style="font-size:.7rem;color:var(--text-tertiary)">Assumes a 7% average annual return</div>
            </div>
            <div style="text-align:right">
              <div style="font-size:.95rem;font-weight:700;color:var(--positive);font-family:var(--font-mono)">${esc(projectedDateLabel)}</div>
              <div style="font-size:.7rem;color:var(--text-tertiary)">
                ${progressData?.future?.projections?.moderate === Infinity ? 'Never' : `${progressData?.future?.projections?.moderate} months`}
              </div>
            </div>
          </div>
        </div>

        <div style="border-top:1px solid var(--bg-tertiary);padding-top:var(--space-3)">
          <div style="display:flex;justify-content:space-between;font-size:.75rem;margin-bottom:4px">
            <span style="color:var(--text-secondary)">Roth IRA Contributions (${currentYear})</span>
            <span style="color:var(--text-primary);font-weight:600">${fmt$(rothThisYear)} / ${fmt$(rothLimit)}</span>
          </div>
          <div class="progress-bar" style="height:6px;background:var(--bg-tertiary);border-radius:99px">
            <div class="progress-fill" style="width:${rothPct}%;height:100%;border-radius:99px;background:var(--positive)"></div>
          </div>
          <div style="display:flex;justify-content:space-between;font-size:.7rem;color:var(--text-tertiary);margin-top:2px">
            <span>${rothPct}% funded</span>
            <span>${fmt$(rothRemaining)} remaining limit</span>
          </div>
        </div>
      </div>

    </div>`;

  // ── WIRE INTERACTIONS ──────────────────────────────────────────────────

  // Voice details toggle
  el.querySelector('#voice-expand-btn')?.addEventListener('click', () => {
    const details = el.querySelector('#voice-details');
    const label = el.querySelector('#voice-expand-btn span');
    const icon = el.querySelector('#voice-expand-icon');
    if (details.style.display === 'none') {
      details.style.display = 'block';
      label.textContent = 'Hide Status Breakdown';
      icon.textContent = '↑';
    } else {
      details.style.display = 'none';
      label.textContent = 'Show Status Breakdown';
      icon.textContent = '↓';
    }
  });

  el.querySelectorAll('.task-check').forEach(taskEl => {
    taskEl.addEventListener('click', async () => {
      const taskId = taskEl.dataset.taskId;
      const isDone = taskEl.classList.contains('checked');
      taskEl.classList.toggle('checked', !isDone);
      try {
        await api.tasks.update(taskId, { status: isDone ? 'open' : 'done' });
      } catch {
        taskEl.classList.toggle('checked', isDone);
      }
    });
  });

  document.getElementById('view-all-tasks')?.addEventListener('click', (e) => {
    e.preventDefault();
    const planNav = document.querySelector('[data-tab="plan"]');
    if (planNav) planNav.click();
  });

  document.getElementById('add-first-task')?.addEventListener('click', (e) => {
    e.preventDefault();
    if (window.openAddTaskSheet) {
      window.openAddTaskSheet();
    }
  });

  // Checklist actions
  el.querySelectorAll('.checklist-item').forEach(item => {
    item.addEventListener('click', () => {
      const id = item.dataset.id;
      if (item.classList.contains('done')) return;
      if (id === 'income' && window.openIncomeSheet) {
        window.openIncomeSheet();
      } else if (id === 'tasks' && window.openAddTaskSheet) {
        window.openAddTaskSheet();
      } else if (id === 'block' && window.openAddBlockSheet) {
        window.openAddBlockSheet();
      } else if (id === 'expense' && window.openExpenseSheet) {
        window.openExpenseSheet();
      } else if (id === 'snapshot') {
        const progressNav = document.querySelector('[data-tab="progress"]');
        if (progressNav) {
          progressNav.click();
          setTimeout(() => {
            document.getElementById('add-snapshot-btn')?.click();
          }, 300);
        }
      }
    });
  });

  // Empty state quick-action buttons
  document.getElementById('empty-log-income')?.addEventListener('click', () => {
    if (typeof window.openIncomeSheet === 'function') window.openIncomeSheet();
  });
  document.getElementById('empty-add-block')?.addEventListener('click', () => {
    if (typeof window.openAddBlockSheet === 'function') window.openAddBlockSheet();
  });
  document.getElementById('empty-go-settings')?.addEventListener('click', () => {
    if (typeof window.switchTab === 'function') window.switchTab('settings');
  });

  // Sample data clear
  document.getElementById('clear-sample')?.addEventListener('click', async () => {
    if (!confirm('Remove all sample data and start fresh?')) return;
    await api.onboarding.clearSample();
    window.location.reload();
  });

  // Banner actions
  wireBannerActions(el, data);
}

// ── BETWEEN-SESSION BANNERS ────────────────────────────────────────────────

function snoozeKey(type) {
  return `snooze-banner-${type}`;
}

function isSnoozed(type) {
  const until = localStorage.getItem(snoozeKey(type));
  if (!until) return false;
  return new Date(until) > new Date();
}

function snooze(type) {
  const until = new Date();
  until.setHours(until.getHours() + 24);
  localStorage.setItem(snoozeKey(type), until.toISOString());
}

function buildBanner(data, nwData, settingsData, reviewedThisWeek, weekPlanned, unscheduledHighPriorityCount) {
  const today   = new Date();
  const dayOfWk = today.getDay(); // 0=Sun
  const hour    = today.getHours();
  const dom     = today.getDate();

  // Priority order — first match wins
  if (data.payPeriod.isPayday && !isSnoozed('payday')) {
    return `
      <div class="session-banner payday">
        <div class="session-banner-title">💵 Payday — ${fmtDate(data.payPeriod.payday)}</div>
        <div class="session-banner-sub">Log your paycheck to keep your surplus accurate.</div>
        <div class="session-banner-actions">
          <button class="btn btn-primary" id="banner-log-income" style="padding:6px 14px;font-size:.8rem">Log paycheck</button>
          <button class="btn btn-ghost"   id="banner-snooze-payday" style="font-size:.8rem">Snooze 24h</button>
        </div>
      </div>`;
  }

  if (data.payPeriod.isCommission && !isSnoozed('commission')) {
    return `
      <div class="session-banner commission">
        <div class="session-banner-title">📋 Commission window — ${fmtDate(data.payPeriod.payday)}</div>
        <div class="session-banner-sub">Did you receive your commission this pay period?</div>
        <div class="session-banner-actions">
          <button class="btn btn-primary" id="banner-log-commission" style="padding:6px 14px;font-size:.8rem">Log commission</button>
          <button class="btn btn-ghost"   id="banner-no-commission"  style="font-size:.8rem">Not this month</button>
          <button class="btn btn-ghost"   id="banner-snooze-commission" style="font-size:.8rem">Snooze 24h</button>
        </div>
      </div>`;
  }

  if (dom >= 26 && dom <= 28 && !isSnoozed('credit')) {
    const latestSnap = nwData?.slice().sort((a, b) => b.date.localeCompare(a.date))[0];
    const bal = latestSnap?.creditCardDebt;
    return `
      <div class="session-banner credit">
        <div class="session-banner-title">💳 Credit card due soon</div>
        <div class="session-banner-sub">${bal != null ? `Current balance: ${fmt$(bal)}` : 'Update your balance before it\'s due.'}</div>
        <div class="session-banner-actions">
          <button class="btn btn-ghost" id="banner-snooze-credit" style="font-size:.8rem">Snooze 24h</button>
        </div>
      </div>`;
  }

  if (dayOfWk === 0 && hour >= 17 && !reviewedThisWeek && !isSnoozed('review')) {
    return `
      <div class="session-banner review">
        <div class="session-banner-title">📊 Your week is wrapping up</div>
        <div class="session-banner-sub">3-minute reflection on how the week went.</div>
        <div class="session-banner-actions">
          <button class="btn btn-primary" id="banner-start-review" style="padding:6px 14px;font-size:.8rem">Start review</button>
          <button class="btn btn-ghost" id="banner-snooze-review" style="font-size:.8rem">Snooze 24h</button>
        </div>
      </div>`;
  }

  // Reviewed but hasn't planned the week yet, and there's actually something
  // to plan — a reviewed week with zero unscheduled high-priority tasks
  // doesn't need this nudge at all.
  if (reviewedThisWeek && !weekPlanned && unscheduledHighPriorityCount > 0 && !isSnoozed('planning')) {
    return `
      <div class="session-banner">
        <div class="session-banner-title">📋 Plan your week</div>
        <div class="session-banner-sub">${unscheduledHighPriorityCount} high-priority task${unscheduledHighPriorityCount === 1 ? '' : 's'} still need${unscheduledHighPriorityCount === 1 ? 's' : ''} scheduling.</div>
        <div class="session-banner-actions">
          <button class="btn btn-primary" id="banner-start-planning" style="padding:6px 14px;font-size:.8rem">Plan →</button>
          <button class="btn btn-ghost" id="banner-snooze-planning" style="font-size:.8rem">Snooze 24h</button>
        </div>
      </div>`;
  }

  // Stale data banner — 5+ days since last onboarding date is a rough proxy
  if (settingsData?.onboardingDate) {
    const daysSince = Math.floor(
      (new Date() - new Date(settingsData.onboardingDate + 'T00:00:00Z')) / 86_400_000
    );
    if (daysSince >= 5 && !isSnoozed('stale')) {
      return `
        <div class="session-banner">
          <div class="session-banner-title">Your picture may be out of date</div>
          <div class="session-banner-sub">Quick update keeps your surplus and net worth accurate.</div>
          <div class="session-banner-actions">
            <button class="btn btn-ghost" id="banner-log-expense-stale" style="font-size:.8rem">Log expense</button>
            <button class="btn btn-ghost" id="banner-snooze-stale" style="font-size:.8rem">Snooze 24h</button>
          </div>
        </div>`;
    }
  }

  return '';
}

function wireBannerActions(el, data) {
  const on = (id, fn) => el.querySelector(`#${id}`)?.addEventListener('click', fn);

  on('banner-snooze-payday',     () => { snooze('payday');     el.querySelector('.session-banner')?.remove(); });
  on('banner-snooze-commission', () => { snooze('commission'); el.querySelector('.session-banner')?.remove(); });
  on('banner-snooze-credit',     () => { snooze('credit');     el.querySelector('.session-banner')?.remove(); });
  on('banner-snooze-review',     () => { snooze('review');     el.querySelector('.session-banner')?.remove(); });
  on('banner-snooze-stale',      () => { snooze('stale');      el.querySelector('.session-banner')?.remove(); });
  on('banner-snooze-planning',   () => { snooze('planning');   el.querySelector('.session-banner')?.remove(); });

  on('banner-log-income', () => {
    if (window.openIncomeSheet) {
      window.openIncomeSheet();
    }
  });

  on('banner-no-commission', async () => {
    const today = new Date().toISOString().split('T')[0];
    await api.income.commission.create({
      forMonth: today.slice(0, 7),
      status:   'confirmed_none',
    }).catch(() => {});
    el.querySelector('.session-banner')?.remove();
  });

  on('banner-log-commission', () => {
    if (window.openCommissionSheet) {
      window.openCommissionSheet();
    }
  });

  on('banner-log-expense-stale', () => {
    if (window.openExpenseSheet) {
      window.openExpenseSheet();
    }
  });

  on('banner-start-review', () => {
    if (window.openWeeklyReviewSheet) {
      window.openWeeklyReviewSheet();
    }
  });

  on('banner-start-planning', () => {
    if (window.openPlanningModeSheet) {
      window.openPlanningModeSheet();
    }
  });
}

// ── FIRST-WEEK CHECKLIST ───────────────────────────────────────────────────

function buildChecklist(settingsData, income, tasks, expenses, nwSnapshots, customBlocks, schoolBlocks) {
  if (!settingsData?.onboardingDate) return '';

  const daysSinceOnboarding = Math.floor(
    (new Date() - new Date(settingsData.onboardingDate + 'T00:00:00Z')) / 86_400_000
  );
  if (daysSinceOnboarding > 7) return ''; // Gone after 7 days

  const onboardDate = settingsData.onboardingDate;

  const items = [
    {
      id:    'income',
      label: 'Log your first paycheck',
      done:  (income || []).some(e => e.payDate >= onboardDate),
      action:'progress',
    },
    {
      id:    'tasks',
      label: 'Add 3 tasks for this week',
      done:  (tasks || []).filter(t => t.status !== 'archived' && t.createdAt >= onboardDate).length >= 3,
      action:'plan',
    },
    {
      id:    'block',
      label: 'Schedule a time block',
      done:  (customBlocks || []).length > 0 || (schoolBlocks || []).length > 0,
      action:'plan',
    },
    {
      id:    'expense',
      label: 'Log one expense',
      done:  (expenses || []).some(e => e.date >= onboardDate),
      action:'progress',
    },
    {
      id:    'snapshot',
      label: 'Take your first net worth snapshot',
      done:  (nwSnapshots || []).length >= 2, // seed creates 1, user needs to add another
      action:'progress',
    },
  ];

  const done  = items.filter(i => i.done).length;
  const total = items.length;
  if (done === total) return ''; // All done — hide permanently

  const pct = Math.round((done / total) * 100);

  return `
    <div class="checklist-card">
      <div class="checklist-header">
        <span class="checklist-title">Get started</span>
        <span class="checklist-count">${done} / ${total}</span>
      </div>
      <div class="progress-bar" style="margin-bottom:var(--space-4)">
        <div class="progress-fill" style="width:${pct}%;background:var(--accent)"></div>
      </div>
      ${items.map(item => `
        <div class="checklist-item${item.done ? ' done' : ''}" data-action="${item.action}" data-id="${item.id}">
          <div class="checklist-check">${item.done ? '✓' : ''}</div>
          <span>${item.label}</span>
        </div>`).join('')}
    </div>`;
}

// ── HELPERS ───────────────────────────────────────────────────────────────

function skeleton() {
  return `
    <div style="max-width:720px;margin:0 auto">
      <div class="skeleton" style="height:40px;border-radius:10px;margin-bottom:16px"></div>
      <div class="skeleton" style="height:120px;border-radius:16px;margin-bottom:16px"></div>
      <div class="skeleton" style="height:200px;border-radius:16px"></div>
    </div>`;
}
